import React from 'react';
import { tierFor, type MilestoneMotif } from '@/lib/milestones';

interface MilestoneEmblemProps {
  level: number;
  size?: number;
  state?: 'earned' | 'current' | 'locked';
}

const Motif: React.FC<{ motif: MilestoneMotif; deep: string }> = ({
  motif,
  deep,
}) => {
  switch (motif) {
    case 'compass':
      return (
        <>
          <path
            d="M0,-30 L8,-8 L30,0 L8,8 L0,30 L-8,8 L-30,0 L-8,-8 Z"
            fill="#fff"
            opacity="0.95"
          />
          <circle r="5" fill={deep} />
        </>
      );
    case 'star':
      return (
        <path
          d="M0,-26 L6.5,-8.9 L24.7,-8 L10.5,3.4 L15.3,21 L0,11 L-15.3,21 L-10.5,3.4 L-24.7,-8 L-6.5,-8.9 Z"
          fill="#fff"
          opacity="0.95"
        />
      );
    case 'robot':
      return (
        <>
          <line x1="0" y1="-20" x2="0" y2="-30" stroke="#fff" strokeWidth="3" />
          <circle cx="0" cy="-32" r="4" fill="#fff" />
          <rect x="-22" y="-18" width="44" height="38" rx="11" fill="#fff" />
          <circle cx="-9" cy="-2" r="5" fill={deep} />
          <circle cx="9" cy="-2" r="5" fill={deep} />
          <path
            d="M-9,9 Q0,17 9,9"
            stroke={deep}
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </>
      );
    case 'database':
      return (
        <>
          <path
            d="M-24,-16 A24,8 0 0 0 24,-16 L24,14 A24,8 0 0 1 -24,14 Z"
            fill="#fff"
          />
          <ellipse
            cx="0"
            cy="-16"
            rx="24"
            ry="8"
            fill="#fff"
            stroke={deep}
            strokeWidth="2"
          />
          <path
            d="M-23,-1 A24,8 0 0 0 23,-1"
            stroke={deep}
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M-22,12 A24,8 0 0 0 22,12"
            stroke={deep}
            strokeWidth="2"
            fill="none"
          />
        </>
      );
    case 'shield':
      return (
        <>
          <path
            d="M0,-28 L24,-18 L24,4 Q24,22 0,30 Q-24,22 -24,4 L-24,-18 Z"
            fill="#fff"
          />
          <path
            d="M-11,2 L-3,11 L13,-9"
            stroke={deep}
            strokeWidth="4.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'neural':
      return (
        <>
          <g stroke="#fff" strokeWidth="1.6" opacity="0.6">
            <line x1="-22" y1="-16" x2="0" y2="-10" />
            <line x1="-22" y1="-16" x2="0" y2="10" />
            <line x1="-22" y1="0" x2="0" y2="-10" />
            <line x1="-22" y1="0" x2="0" y2="10" />
            <line x1="-22" y1="16" x2="0" y2="-10" />
            <line x1="-22" y1="16" x2="0" y2="10" />
            <line x1="0" y1="-10" x2="22" y2="0" />
            <line x1="0" y1="10" x2="22" y2="0" />
          </g>
          <g fill="#fff">
            <circle cx="-22" cy="-16" r="4.5" />
            <circle cx="-22" cy="0" r="4.5" />
            <circle cx="-22" cy="16" r="4.5" />
            <circle cx="0" cy="-10" r="4.5" />
            <circle cx="0" cy="10" r="4.5" />
            <circle cx="22" cy="0" r="5.5" />
          </g>
        </>
      );
    case 'gear':
      return (
        <>
          <g fill="#fff">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
              <rect
                key={a}
                x="-3.5"
                y="-33"
                width="7"
                height="13"
                rx="2"
                transform={`rotate(${a})`}
              />
            ))}
          </g>
          <circle r="19" fill="none" stroke="#fff" strokeWidth="5" />
          <path
            d="M0,-11 L3,-3 L11,0 L3,3 L0,11 L-3,3 L-11,0 L-3,-3 Z"
            fill="#fff"
          />
        </>
      );
    case 'crown':
      return (
        <>
          <path
            d="M-30,30 Q-48,4 -34,-20"
            stroke="#E8C766"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M30,30 Q48,4 34,-20"
            stroke="#E8C766"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <path d="M0,-22 L18,-5 L0,28 L-18,-5 Z" fill="#fff" />
          <path
            d="M-18,-5 L18,-5 M0,-22 L0,28 M-9,-13.5 L9,-13.5"
            stroke="#B9A6F0"
            strokeWidth="1.5"
            fill="none"
          />
          <path
            d="M-22,-46 L-12,-60 L0,-49 L12,-60 L22,-46 L17,-34 L-17,-34 Z"
            fill="#E8C766"
            stroke={deep}
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <circle cx="-12" cy="-60" r="3" fill={deep} />
          <circle cx="12" cy="-60" r="3" fill={deep} />
        </>
      );
  }
};

const MilestoneEmblem: React.FC<MilestoneEmblemProps> = ({
  level,
  size = 64,
  state = 'earned',
}) => {
  const tier = tierFor(level);
  const locked = state === 'locked';
  const gradId = `me-grad-${tier.level}-${state}`;
  const light = locked ? '#E6E4DE' : tier.light;
  const deep = locked ? '#B4B2A9' : tier.deep;
  const rim = locked ? '#B4B2A9' : tier.deep;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      role="img"
      aria-label={`${tier.name} emblem, ${tier.title}, L${tier.level}${locked ? ', locked' : ''}`}
    >
      <defs>
        <radialGradient id={gradId} cx="38%" cy="30%" r="78%">
          <stop offset="0" stopColor={light} />
          <stop offset="1" stopColor={deep} />
        </radialGradient>
      </defs>
      <g transform="translate(60,60)">
        <circle r="56" fill={`url(#${gradId})`} />
        <circle r="56" fill="none" stroke={rim} strokeWidth="3" />
        <ellipse cx="-16" cy="-24" rx="20" ry="11" fill="#fff" opacity="0.18" />
        {locked ? (
          <g data-locked="true" fill="#fff">
            <rect x="-15" y="-4" width="30" height="24" rx="5" />
            <path
              d="M-9,-4 V-12 a9,9 0 0 1 18,0 V-4"
              fill="none"
              stroke="#fff"
              strokeWidth="4"
            />
          </g>
        ) : (
          <Motif motif={tier.motif} deep={deep} />
        )}
      </g>
      {state === 'current' && (
        <circle
          cx="60"
          cy="60"
          r="58"
          fill="none"
          stroke="#059669"
          strokeWidth="3"
        />
      )}
    </svg>
  );
};

export default MilestoneEmblem;
